import type { JSX } from 'react';
import { useState } from 'react';
import { SegmentedControl, Switch, Table, Text } from '@mantine/core';
import { costsApi, type CostPlan, type CostStage, type CostStageTotals } from '../../api';
import { COST_STAGES, formatAmount, formatPercent, formatQuantity } from '../../format';
import { Section } from '../../ui/Section';
import { usePageDownloads } from '../../export/downloads';

/**
 * The variants side by side: one column each, the cost groups down the side,
 * then the totals with risk, gross and range, then the ratios. The difference
 * to the primary variant is written under each figure, so the table reads
 * without a second one.
 */
export function CompareView({ projectId, plan, stage, onStage }: {
  projectId: string;
  plan: CostPlan;
  stage: CostStage;
  onStage: (stage: CostStage) => void;
}): JSX.Element {
  usePageDownloads('costs-compare', [{ key: 'compare-csv', label: 'Variantenvergleich als CSV', hint: 'die gewählte Stufe, alle Varianten', href: costsApi.compareCsvUrl(projectId, stage) }]);
  const [deep, setDeep] = useState(false);
  const results = plan.results;
  const heads = results.map((r) => plan.variants.find((v) => v.id === r.variantId));
  const primaryIndex = Math.max(0, results.findIndex((r) => r.variantId === plan.primaryVariantId));
  const maps = results.map((r) => new Map(r.rollup.groups.map((g) => [g.code, g])));
  const depthOf = new Map(results[0]?.rollup.groups.map((g) => [g.code, g.depth]) ?? []);
  const rows = plan.groups.filter((g) => deep || (depthOf.get(g.code) ?? 0) === 0);
  const gross = (t: CostStageTotals): number | null => (t.withRisk === null ? null : Math.round(t.withRisk * (1 + plan.vatRate / 100) * 100) / 100);
  const ratios = plan.values.filter((v) => v.kind === 'ratio');
  // An estimate reads in whole euros; the exact figures are in the CSV.
  const whole = (v: number): string => formatAmount(v, 0);
  const ratio = (v: number): string => formatAmount(v, Math.abs(v) >= 100 ? 0 : 2);

  const diff = (values: (number | null)[], i: number): JSX.Element | null => {
    const base = values[primaryIndex];
    const v = values[i];
    if (i === primaryIndex || base === null || base === undefined || v === null || v === undefined || base === 0) return null;
    const d = ((v - base) / Math.abs(base)) * 100;
    if (Math.abs(d) < 0.05) return null;
    return <Text span size="xs" c="dimmed" display="block">{formatPercent(d, true)}</Text>;
  };
  const row = (label: string, values: (number | null)[], opts: { bold?: boolean; code?: string; format?: (v: number) => string } = {}): JSX.Element => (
    <Table.Tr key={label + (opts.code ?? '')} style={opts.bold ? { borderTop: '2px solid var(--mantine-color-default-border)' } : undefined}>
      <Table.Td w={80}><Text size="sm" ff="monospace" c="dimmed">{opts.code}</Text></Table.Td>
      <Table.Td><Text size="sm" fw={opts.bold ? 700 : undefined}>{label}</Text></Table.Td>
      {values.map((v, i) => (
        <Table.Td key={i} ta="right">
          <Text size="sm" fw={opts.bold ? 700 : undefined} c={v === null ? 'dimmed' : undefined}>{v === null ? '—' : (opts.format ?? whole)(v)}</Text>
          {diff(values, i)}
        </Table.Td>
      ))}
    </Table.Tr>
  );

  return (
    <Section
      title="Variantenvergleich"
      description={`Beträge netto in ganzen €, wo nicht anders angegeben. Unter den Zahlen die Abweichung zu ${heads[primaryIndex]?.code ?? 'der Hauptvariante'} (Kostenkontrolle).`}
      actions={
        <>
          <Switch size="xs" label="alle Ebenen" checked={deep} onChange={(e) => setDeep(e.currentTarget.checked)} />
        </>
      }
    >
      <SegmentedControl size="xs" value={stage} onChange={(v) => onStage(v as CostStage)} data={COST_STAGES.map((s) => ({ value: s.key, label: s.short }))} />
      <Table.ScrollContainer minWidth={300 + results.length * 150}>
        <Table verticalSpacing={3} className="pst-tabular" highlightOnHover>
          <Table.Thead>
            <Table.Tr>
              <Table.Th w={80}>KG</Table.Th>
              <Table.Th>Bezeichnung</Table.Th>
              {heads.map((h, i) => (
                <Table.Th key={h?.id ?? i} ta="right" w={150} title={h ? [`${h.code} ${h.name}`, h.description].filter(Boolean).join(' – ') : undefined}>
                  {/* Long names are cut to three lines; the full name is in the tooltip. */}
                  <Text span size="sm" fw={700} lineClamp={3} style={{ wordBreak: 'break-word' }}>{h ? `${h.code} ${h.name}` : 'Summe'}{i === primaryIndex && heads.length > 1 ? ' ·' : ''}</Text>
                </Table.Th>
              ))}
            </Table.Tr>
          </Table.Thead>
          <Table.Tbody>
            {rows.map((g) => row(g.name, maps.map((m) => m.get(g.code)?.stages[stage] ?? null), { code: g.code }))}
            {row('Summe netto', results.map((r) => r.totals[stage].net), { bold: true })}
            {row('Risikozuschlag', results.map((r) => r.totals[stage].risk))}
            {row('Summe netto mit Risiko', results.map((r) => r.totals[stage].withRisk), { bold: true })}
            {row(`Brutto (${formatQuantity(plan.vatRate)} % USt.)`, results.map((r) => gross(r.totals[stage])))}
            {row('Bandbreite von', results.map((r) => r.totals[stage].low))}
            {row('Bandbreite bis', results.map((r) => r.totals[stage].high))}
            {ratios.length > 0 && (
              <Table.Tr><Table.Td colSpan={2 + results.length}><Text size="xs" fw={600} c="dimmed" pt={6}>Kennzahlen</Text></Table.Td></Table.Tr>
            )}
            {ratios.map((v) => row(`${v.label ?? v.name}${v.unit ? ` (${v.unit})` : ''}`, results.map((r) => r.ratios[stage][v.name]?.value ?? null), { code: v.name, format: ratio }))}
          </Table.Tbody>
        </Table>
      </Table.ScrollContainer>
    </Section>
  );
}
